<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import { LISTING_REASONS } from '../utils/imovelShowConstants';

defineProps({
  summary: {
    type: Object,
    default: () => ({}),
  },
  reviewRows: {
    type: Array,
    default: () => [],
  },
  listingStatusLabel: {
    type: String,
    default: '',
  },
  rentalAdministratorLabel: {
    type: String,
    default: '',
  },
  listingSourceLabel: {
    type: String,
    default: '',
  },
  visitasPermitidasLabel: {
    type: String,
    default: '',
  },
  lifecycleLoading: {
    type: String,
    default: '',
  },
  mediaQaCanPublish: {
    type: Boolean,
    default: false,
  },
  publishPreviewReady: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['prepareAction', 'runAction']);

const lifecycleReason = defineModel('lifecycleReason', {
  type: String,
  required: true,
});
const suspendDays = defineModel('suspendDays', {
  type: Number,
  required: true,
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="mb-5 grid gap-3">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <p class="text-[13px] font-semibold text-lais-text">Revisão publicável</p>
      <LaisStatusBadge
        :label="summary.label"
        :tone="summary.tone"
        size="sm"
        dot
      />
    </div>
    <dl class="grid gap-2 text-[12px] sm:grid-cols-2">
      <div
        v-for="row in reviewRows"
        :key="`publishable-${row.label}`"
        class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft px-3 py-2"
      >
        <dt class="text-lais-text-muted">{{ row.label }}</dt>
        <dd class="mt-0.5 break-words font-semibold text-lais-text">
          {{ row.value }}
        </dd>
      </div>
    </dl>
  </section>

  <div class="flex flex-col gap-1 text-[13px]">
    <p class="flex items-center justify-between gap-3">
      <span class="text-lais-text-muted">Status</span>
      <span class="font-semibold text-lais-text">{{ listingStatusLabel }}</span>
    </p>
    <p class="flex items-center justify-between gap-3">
      <span class="text-lais-text-muted">Gestão</span>
      <span class="font-semibold text-lais-text">{{
        rentalAdministratorLabel
      }}</span>
    </p>
    <p class="flex items-center justify-between gap-3">
      <span class="text-lais-text-muted">Fonte</span>
      <span class="font-semibold text-lais-text">{{ listingSourceLabel }}</span>
    </p>
    <p class="flex items-center justify-between gap-3">
      <span class="text-lais-text-muted">Visitas</span>
      <span class="font-semibold text-lais-text">{{
        visitasPermitidasLabel
      }}</span>
    </p>
  </div>

  <div class="mt-4 grid gap-3">
    <label
      for="listing-lifecycle-reason"
      class="text-[12px] font-semibold text-lais-text"
    >
      Motivo operacional
    </label>
    <select
      id="listing-lifecycle-reason"
      v-model="lifecycleReason"
      class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
    >
      <option
        v-for="reason in LISTING_REASONS"
        :key="reason.value"
        :value="reason.value"
      >
        {{ reason.label }}
      </option>
    </select>

    <label
      for="listing-suspend-days"
      class="text-[12px] font-semibold text-lais-text"
    >
      Dias de suspensão
    </label>
    <input
      id="listing-suspend-days"
      v-model.number="suspendDays"
      type="number"
      min="1"
      max="90"
      class="h-10 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text focus:border-lais-primary focus:outline-none"
    />
  </div>

  <div class="mt-4 grid grid-cols-2 gap-2">
    <LaisPrimaryButton
      variant="primary"
      size="sm"
      icon="i-lucide-radio-tower"
      :is-loading="lifecycleLoading === 'publish'"
      :disabled="!!lifecycleLoading || !mediaQaCanPublish"
      @click="emit('prepareAction', 'publish')"
    >
      {{ publishPreviewReady ? 'Publicar' : 'Gerar prévia' }}
    </LaisPrimaryButton>
    <LaisPrimaryButton
      variant="ghost"
      size="sm"
      icon="i-lucide-eye-off"
      :is-loading="lifecycleLoading === 'unpublish'"
      :disabled="!!lifecycleLoading"
      @click="emit('runAction', 'unpublish')"
    >
      Despublicar
    </LaisPrimaryButton>
    <LaisPrimaryButton
      variant="ghost"
      size="sm"
      icon="i-lucide-pause-circle"
      :is-loading="lifecycleLoading === 'suspend'"
      :disabled="!!lifecycleLoading"
      @click="emit('runAction', 'suspend')"
    >
      Suspender
    </LaisPrimaryButton>
    <LaisPrimaryButton
      variant="ghost"
      size="sm"
      icon="i-lucide-repeat-2"
      :is-loading="lifecycleLoading === 'relist'"
      :disabled="!!lifecycleLoading"
      @click="emit('runAction', 'relist')"
    >
      Reanunciar
    </LaisPrimaryButton>
  </div>
</template>
